"use client";
// @ts-strict-ignore
import * as React from "react";
import debounce from "lodash/debounce";

import { mixpanelClient } from "analytics/mixpanel";

export const useBodyLock = (condition: boolean) => {
  React.useEffect(() => {
    const { body } = document;
    if (condition) {
      const scrollBarGap =
        window.innerWidth - document.documentElement.clientWidth;
      body.style.overflow = "hidden";
      body.style.paddingRight = `${scrollBarGap}px`;
    } else {
      body.style.overflow = "visible";
      body.style.paddingRight = "0";
    }
  }, [condition]);
};

export interface UseMixpanelTrackScrollingConfig {
  mixpanelEventName: string;
  properties: Record<string, any>;
}
export const useMixpanelTrackScrolling = (
  config: UseMixpanelTrackScrollingConfig
) => {
  const [scrolled, setScrolledState] = React.useState(0);
  const [rangeScrolled, setRangeScrolledState] = React.useState(0);

  const scrolledRef = React.useRef(scrolled);
  const rangeScrolledRef = React.useRef(rangeScrolled);

  React.useEffect(() => {
    window.addEventListener("scroll", trackScrolling);

    return () => {
      window.removeEventListener("scroll", trackScrolling);
    };
  });

  /* 
    Caluclates the percentage of the document that the client has scrolled up to.
  */
  const calculateScroll = () => {
    // The vertical scroll position value at the top of the client's viewport.
    const scrollTop = window.scrollY;
    // The height value of the client's viewport
    const windowHeight = window.innerHeight;
    // The total height value of the document.
    const docHeight = getDocHeight();

    // Offset the total doc scroll by the height of the viewport.
    // This essentially brings our scrollTop to the bottom of the viewport
    const totalDocScroll = docHeight - windowHeight;

    return Math.floor((scrollTop / totalDocScroll) * 100);
  };

  const setRangeScrolled = (data) => {
    rangeScrolledRef.current = data;
    setRangeScrolledState(data);
  };

  const scrollRanges = {
    0: "<25%",
    25: "<25%",
    50: "25-50%",
    75: "50-75%",
    100: "75-100%"
  };

  const getScrollRange = () => {
    if (scrolledRef.current < 25) return 25;
    if (scrolledRef.current < 50) return 50;
    if (scrolledRef.current < 75) return 75;
    return 100;
  };

  const getDocHeight = () =>
    Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );

  const trackScrolling = debounce((e) => {
    e.preventDefault();
    const actualScrolled = calculateScroll();
    if (actualScrolled > scrolledRef.current) setScrolled(actualScrolled);
    if (isOverScrollRange()) {
      const properties = {
        ...config.properties,
        bucket: scrollRanges[rangeScrolledRef.current]
      };
      mixpanelClient.track(config.mixpanelEventName, properties);
    }
  }, 150);

  const isOverScrollRange = () => {
    const actualScrollRange = getScrollRange();
    if (actualScrollRange > rangeScrolledRef.current) {
      setRangeScrolled(actualScrollRange);
      return true;
    }
    return false;
  };

  const setScrolled = (data) => {
    scrolledRef.current = data;
    setScrolledState(data);
  };
};
